import React from 'react';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { getStatsIcon } from '../../../../../shared/utils/utils';
import { t } from 'i18next';
import { CustomSkeletonTile } from './components/skeleton-tile/CustomSkeletonTile';
import { TrendTag } from '../trend-tag/TrendTag';

interface TileBoxProps {
  hasData: boolean;
  value: number | string | null | undefined;
  trendValue: number | string;
  label: string;
  tooltipText: string;
  suffix?: string;
  // Optional secondary line rendered below the main value (e.g. the % under the
  // Visibility text interpretation on the V2 preview page).
  subValue?: string;
  // Fresh project still collecting → show a "computing" tile instead of a skeleton.
  collecting?: boolean;
  // Rotating "what we're doing right now" message shown in the value slot while collecting.
  computingMessage?: string;
}

export const TileBox: React.FC<TileBoxProps> = ({
  hasData,
  value,
  trendValue,
  label,
  tooltipText,
  suffix = '',
  subValue,
  collecting = false,
  computingMessage,
}) => {
  if (!hasData) {
    // Fresh project still collecting → keep the real tile layout (name + badge) but show a rotating
    // "what we're doing" message where the value will be, so it reads as work-in-progress (livelier
    // than a spinner), on-brand, instead of a grey skeleton. `key` re-mounts it so each line fades in.
    return collecting ? (
      <div className="tile-box tile-box--computing">
        <div className="tile-box__box">
          <label className="tile-box__name">
            {t(label)}
            <CustomTooltip tooltipText={tooltipText} />
          </label>
          <div className="tile-box__computing">
            {/* Dash stand-in for the value while it's still computing; a soft light band sweeps across
                it (CSS shimmer) to signal work-in-progress. */}
            <span className="tile-box__calc" aria-hidden="true">
              –
            </span>
            <span className="tile-box__computing-msg" key={computingMessage}>
              {computingMessage}
            </span>
          </div>
        </div>
      </div>
    ) : (
      <CustomSkeletonTile />
    );
  }

  return (
    <div className="tile-box">
      <div className="tile-box__box">
        <label className="tile-box__name">
          {t(label)}
          <CustomTooltip tooltipText={tooltipText} />
        </label>
        <label className="tile-box__value">
          {`${value}${suffix}`}
          {trendValue !== 0 && <TrendTag trendValue={trendValue} suffix={suffix} />}
        </label>
        {subValue && <span className="tile-box__subvalue">{subValue}</span>}
      </div>
    </div>
  );
};
